import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DownloadSection } from './shared/DownloadSection';
import { FeedbackWidget, ExpiredLinkIcon } from './shared/FeedbackWidget';
import { LoadingWidget } from './shared/LoadingWidget';
import { NetworkErrorWidget } from './shared/NetworkErrorWidget';
import { PaymentData } from '../types/PaymentData';
import { parseDestination } from './paymentLink';

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://pebla-backend-production.up.railway.app';

type ScreenState = 'loading' | 'expired' | 'network-error';

export const JoinScreen: React.FC = () => {
    const [screenState, setScreenState] = useState<ScreenState>('loading');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const fetchPaymentLink = useCallback(async (token: string) => {
        setScreenState('loading');
        try {
            const response = await fetch(`${BASE_URL}/api/payment-links/${token}`);
            const result = await response.json();

            console.log('Payment link fetch result:', result);

            if (result.success && result.data) {
                const paymentData: PaymentData = {
                    sender: {
                        name: result.data.sender?.name || '',
                        phoneNumber: result.data.sender?.phoneNumber || '',
                    },
                    recipient: {
                        name: result.data.recipient?.name || '',
                        phoneNumber: result.data.recipient?.phoneNumber || '',
                    },
                    amount: result.data.amount ?? 0,
                    currency: result.data.currency || 'RWF',
                    destination: parseDestination(result.data.destination),
                };
                navigate('/payment', { state: paymentData });
            } else {
                setScreenState('expired');
            }
        } catch (error) {
            console.error('Failed to fetch payment link:', error);
            setScreenState('network-error');
        }
    }, [navigate]);

    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            fetchPaymentLink(token);
        } else {
            setScreenState('expired');
        }
    }, [searchParams, fetchPaymentLink]);

    const handleRetry = () => {
        const token = searchParams.get('token');
        if (token) {
            fetchPaymentLink(token);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            padding: '20px 16px',
            maxWidth: '400px',
            margin: '0 auto',
            boxSizing: 'border-box',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <img
                    src="/images/icons/pe.svg"
                    alt="Logo"
                    style={{ width: '40px', height: '40px' }}
                />
            </div>

            {/* Main content — vertically centered */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {screenState === 'loading' && <LoadingWidget message="Loading payment details..." />}
                {screenState === 'expired' && (
                    <FeedbackWidget
                        icon={<ExpiredLinkIcon />}
                        title="This link expired"
                        message="Payment links are valid for 24 hours."
                    />
                )}
                {screenState === 'network-error' && <NetworkErrorWidget onRetry={handleRetry} />}
            </div>

            {/* Download section always visible */}
            <DownloadSection />
        </div>
    );
};